import React, { useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCategoryContext } from '../context/CategoryContext';
import { MECHANICAL_SEAL_CATEGORY_NAME } from '../config/api';
import { WHATSAPP_NUMBER, WHATSAPP_DEFAULT_MESSAGE } from '../utils/whatsappUtils';

function Footer() {
  const navigate = useNavigate();
  const { categories } = useCategoryContext();

  // Get top 4 categories from context (already fetched at app level)
  const topCategories = useMemo(() => {
    if (categories && categories.length > 0) {
      return categories.slice(0, 4);
    }
    // Fallback to default categories if context data not available yet
    return [
      { id: 1, name: MECHANICAL_SEAL_CATEGORY_NAME },
      { id: 2, name: "Aluminium Pulleys" },
      { id: 3, name: "Water Pump Parts" },
      { id: 4, name: "Generator AVRs" }
    ];
  }, [categories]);

  // Memoized quick links
  const quickLinks = useMemo(() => [
    { label: "Home", path: "/" },
    { label: "Category", path: "/category" },
    { label: "Products", path: "/product" },
    { label: "About Us", path: "/about" },
  ], []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleCategoryClick = useCallback((categoryName) => {
    // Navigate to category page
    navigate('/category');
    
    // Small delay to ensure page loads, then trigger search
    setTimeout(() => {
      // Dispatch custom event with category name
      window.dispatchEvent(new CustomEvent('footerCategorySearch', { 
        detail: { categoryName } 
      }));
    }, 100);
    
    scrollToTop();
  }, [navigate, scrollToTop]);

  return (
    <footer className="px-4 py-8 text-white bg-slate-800 md:px-16">
      {/* Top Grid */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
        {/* Brand */}
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col gap-0 items-start mb-2">
            <img
              src="/footer.png"
              alt="Al Sayyed Electro Mech Engineering Logo"
              className="h-auto w-auto max-w-[160px] object-contain"
            />
          </div>
          <p className="text-sm leading-snug text-gray-300">
          Supplier of high-quality motor/ water pump Parts for Domestic, Commercial and industrial applications.
          </p>
        </div>

        {/* Quick Links */}
        <div className="w-500">
          <h3 className="mb-3 font-semibold text-white">Quick Links</h3>
          <ul className="space-y-2">
            {quickLinks.map((item) => (
              <li key={item.label}>
                <Link
                  to={item.path}
                  onClick={scrollToTop}
                  className="text-gray-200 transition-all hover:text-white"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Categories */}
        <div>
          <h3 className="mb-3 font-semibold text-white">Categories</h3>
          <ul className="space-y-2">
            {topCategories.map((category) => (
              <li key={category.id}>
                <Link
                  to={`/category?search=${encodeURIComponent(category.name)}`}
                  onClick={(e) => { e.preventDefault(); handleCategoryClick(category.name); }}
                  className="text-gray-200 transition-all hover:text-white"
                >
                  {category.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* QR Code Section */}
        <div className="flex flex-col items-center">
          <h3 className="mb-3 font-semibold text-white">Contact Us</h3>
          <div 
            className="relative transition-transform duration-300 cursor-pointer group hover:scale-105"
            onClick={() => {
              const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(WHATSAPP_DEFAULT_MESSAGE)}`;
              window.open(whatsappUrl, '_blank');
            }}
            title="Click to contact us on WhatsApp"
          >
            <img
              src="/home/qr.png"
              alt="WhatsApp QR Code - Click to contact us"
              className="object-cover w-36 h-36 rounded-lg border-2 shadow-lg transition-all duration-300 md:w-44 md:h-44 border-red-600/20 group-hover:border-red-600/40"
            />
            {/* Image overlay effect */}
            <div className="absolute inset-0 bg-gradient-to-t to-transparent rounded-lg transition-all duration-300 from-black/20 group-hover:from-black/10"></div>
            
            {/* Decorative elements */}
            <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-600 rounded-full opacity-80 transition-opacity duration-300 group-hover:opacity-100"></div>
            <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-red-600 rounded-full opacity-60 transition-opacity duration-300 group-hover:opacity-80"></div>
          </div>
          <p className="mt-3 text-xs text-center text-gray-300">Scan to contact us on WhatsApp</p>
        </div>
          
        </div>
      

      {/* Divider */}
      <div className="my-6 border-t border-slate-700"></div>

      {/* Bottom Bar */}
      <div className="flex flex-col justify-between items-center space-y-2 md:flex-row md:space-y-0">
        <p className="text-xs text-gray-400">
          © 2025 Al Sayyeds Enterprise. All rights reserved.
        </p>
        <div className="flex space-x-4 text-xs">
          <Link to="/privacy-policy" className="text-gray-400 transition-all hover:text-white">
            Privacy Policy
          </Link>
          <span className="text-gray-500">|</span>
          <Link to="/terms-of-service" className="text-gray-400 transition-all hover:text-white">
            Terms of Service
          </Link>
        </div>
      </div>
    </footer>
  );
}

// Memoize Footer component to prevent unnecessary re-renders
export default React.memo(Footer);
